import { SbefCode } from '@shared/construction/sbef.model';
import { includesAll, isOneOf } from '../../../helpers/array.helpers';
import { moveLifecycleAResults } from '../../../helpers/conversion-factors.helpers';
import { getElementPropertyResolvedCountByNameOrId } from '../../../helpers/element-property.helpers';
import { IElementCategory } from '../../../models/element-categories.model';
import { ElementPropertyName } from '../../../models/element-property.model';
import { Lifecycle } from '../../../models/lifecycles.model';
import { IElement } from '../../../models/project.model';
import {
  DEFAULT_ENERGY_PRODUCT_ID,
  DISTRICT_HEATING_PRODUCT_ID,
  energyProducts,
} from './energy.model';
import { getSbefCode } from '@shared/helpers/sbef.helpers';

const energyProductIds = energyProducts.map(({ id }) => id);
if (
  !includesAll(
    energyProductIds,
    DEFAULT_ENERGY_PRODUCT_ID,
    DISTRICT_HEATING_PRODUCT_ID,
  )
) {
  throw new Error('Default energy product ids not found in products');
}

/** SBEF codes that are considered A5 lifecycle emissions (energy). Excludes 101 (A5.1 / B6 annual). */
const A5_SBEF_CODES: SbefCode[] = ['102', '103', '104', '105'];

/**
 * SBEF codes where energy quantity displays as kWh, not kWh/yr.
 * Matches {@link A5_SBEF_CODES} today; kept separate so a future display-only
 * exception does not require touching lifecycle logic.
 */
const A5_ENERGY_KWH_DISPLAY_SBEF_CODES: readonly SbefCode[] = A5_SBEF_CODES;

/**
 * Check if the sbef code is an A5 lifecycle emissions (energy). IE a one-time emission.
 * All other codes are considered B6 (yearly emissions)
 * @param element
 * @returns
 */
export const isA5SbefCode = (element: IElement): boolean => {
  const sbef = getSbefCode(
    getElementPropertyResolvedCountByNameOrId(
      element,
      ElementPropertyName.SbefCode,
    ),
  );

  return isOneOf(A5_SBEF_CODES, sbef);
};

/**
 * Whether the energy quantity should display as kWh (A5 consumption) rather than kWh/yr.
 * Excludes 101 (A5.1) which remains annual ({@link isA5SbefCode} lifecycle is also B6 for 101).
 *
 * @param element - Energy element with an SBEF code property
 */
export const isA5EnergyKwhDisplaySbefCode = (element: IElement): boolean => {
  const sbef = getSbefCode(
    getElementPropertyResolvedCountByNameOrId(
      element,
      ElementPropertyName.SbefCode,
    ),
  );

  return isOneOf(A5_ENERGY_KWH_DISPLAY_SBEF_CODES, sbef);
};

/**
 * Move emission in A to B6 (yearly) unless the sbef code is set to one of the A5 codes (one-time emissions)
 * @param results
 * @param context
 * @returns
 */
export const mapResults: IElementCategory['mapResults'] = (
  results,
  { categoryElement },
) => {
  const isA5Sbef = isA5SbefCode(categoryElement);
  return moveLifecycleAResults(results, isA5Sbef ? Lifecycle.A5 : Lifecycle.B6);
};

/**
 * Get the label for the energy property. Used to highlight the property as "Annual energy" instead of "Energy" (default)
 */
export const getQuantityPropertyLabel: IElementCategory['getQuantityPropertyLabel'] =
  (property, element) =>
    property.name === 'energy' && !isA5SbefCode(element)
      ? 'Annual energy'
      : undefined;
